import { createStyles, MantineTheme } from "@mantine/core";
import { ISelectCardStyles, ISelectCardStylesColorProps } from "./types";

function getColors(
  theme: MantineTheme,
  { colorScheme, shouldTitleBeColored, disabled }: ISelectCardStyles,
): ISelectCardStylesColorProps {
  let styles: ISelectCardStylesColorProps = {
    rootHover: {
      backgroundColor: theme.colors.brand[0],
      borderColor: theme.fn.darken(theme.colors.gray[2], 0.15),
    },
    rootSelected: {
      backgroundColor: theme.colors.brand[0],
      borderColor: theme.colors.brand[3],
    },
    title: { color: "inherit" },
  };

  if (colorScheme === "green") {
    styles = {
      ...styles,
      rootHover: {
        backgroundColor: theme.colors.green[0],
        borderColor: theme.colors.green[1],
      },
      rootSelected: {
        backgroundColor: theme.colors.green[0],
        borderColor: theme.colors.green[1],
      },
    };

    if (shouldTitleBeColored) {
      styles = {
        ...styles,
        title: { color: theme.colors.green[1] },
      };
    }
  }

  if (colorScheme === "yellow") {
    styles = {
      ...styles,
      rootHover: {
        backgroundColor: theme.colors.yellow[1],
        borderColor: theme.colors.yellow[3],
      },
      rootSelected: {
        backgroundColor: theme.colors.yellow[1],
        borderColor: theme.colors.yellow[3],
      },
    };

    if (shouldTitleBeColored) {
      styles = {
        ...styles,
        title: { color: theme.colors.yellow[3] },
      };
    }
  }

  if (colorScheme === "purple") {
    styles = {
      ...styles,
      rootHover: {
        backgroundColor: theme.fn.lighten(theme.colors.purple[0], 0.9),
        borderColor: theme.colors.purple[0],
      },
      rootSelected: {
        backgroundColor: theme.fn.lighten(theme.colors.purple[0], 0.9),
        borderColor: theme.colors.purple[0],
      },
    };

    if (shouldTitleBeColored) {
      styles = {
        ...styles,
        title: { color: theme.colors.purple[0] },
      };
    }
  }

  if (colorScheme === "brand") {
    styles = {
      ...styles,
      rootHover: {
        backgroundColor: theme.colors.brand[0],
        borderColor: theme.colors.brand[3],
      },
      rootSelected: {
        backgroundColor: theme.colors.brand[0],
        borderColor: theme.colors.brand[3],
      },
    };

    if (shouldTitleBeColored) {
      styles = {
        ...styles,
        title: { color: theme.colors.brand[4] },
      };
    }
  }

  // disabled
  if (disabled) {
    styles.title.color = theme.colors.gray[8];
  }

  return styles;
}

export const useSelectCardStyles = createStyles(
  (theme, options: ISelectCardStyles) => {
    const { rootHover, rootSelected, title } = getColors(theme, options);
    const { noGrow, disabled, hasDisabledTooltip } = options;

    return {
      wrapper: {
        position: "relative",
        width: "100%",
      },

      root: {
        alignItems: "flex-start",
        borderColor: theme.colors.gray[2],
        color: disabled ? theme.colors.gray[8] : theme.colors.gray[5],
        cursor: "pointer",
        display: "flex",
        height: noGrow ? "auto" : "100%",
        position: "relative",
        textAlign: "left",
        transition: `background-color ${theme.other.animation.duration.interaction}ms, border-color ${theme.other.animation.duration.interaction}ms, color ${theme.other.animation.duration.interaction}ms, opacity ${theme.other.animation.duration.interaction}ms`,
        userSelect: "none",
        width: "100%",

        "&:hover": {
          ...rootHover,
          boxShadow: theme.shadows.sm,
          color: theme.colors.gray[6],
        },

        "&[data-selected=true]": {
          ...rootSelected,
          boxShadow: theme.shadows.sm,
          color: theme.colors.gray[5],
        },

        "&[disabled]": {
          borderColor: theme.colors.gray[2],
          backgroundColor: theme.colors.gray[1],
          cursor: hasDisabledTooltip ? "auto" : "default",
          opacity: hasDisabledTooltip ? 1 : theme.other.disabled.opacity,
          pointerEvents: hasDisabledTooltip ? "auto" : "none",
        },

        "&[data-clickable=false]": {
          cursor: "default",
          pointerEvents: "none",
        },
      },

      title,

      tooltip: {
        bottom: theme.spacing.xs,
        display: "inline-block",
        position: "absolute",
        right: theme.spacing.xs,
        zIndex: 1,
      },

      tooltipIcon: {
        appearance: "none",
        backgroundColor: "transparent",
        border: "none",
        fontSize: 0,
        lineHeight: 0,
        margin: 0,
        padding: 0,
        verticalAlign: "bottom",
      },

      disabledTooltip: {
        border: `1px solid ${theme.colors.red[3]}`,
        borderRadius: theme.radius.md,
        opacity: 0,
        transition: `opacity ${theme.other.animation.duration.interaction}ms`,

        "&:hover": { opacity: 1 },
      },
    };
  },
);
